import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { MODULE_PAGE_CSS } from './moduleBannerStyles';

interface InventoryItem {
  id: number;
  property_address: string;
  room_name: string | null;
  item_name: string;
  condition: string;
  purchase_price: number | null;
  purchase_date: string | null;
  model_size: string | null;
  notes: string | null;
  photo_url?: string | null;
}

const CONDITIONS = ['new', 'good', 'fair', 'worn'] as const;
const COMMON_ITEMS = ['Bed', 'Wardrobe', 'Desk', 'Chair', 'TV', 'Sofa', 'Fridge', 'Washing Machine', 'Microwave', 'Kettle', 'Toaster', 'Table', 'Bookshelf', 'Lamp', 'Mirror'];
const ROOM_NAMES = ['Living Room', 'Kitchen', 'Bedroom 1', 'Bedroom 2', 'Bedroom 3', 'Bathroom', 'Garden', 'Hallway', 'Garage'];
const STARTER_KIT = [
  { item_name: 'Bed', room_name: 'Bedroom 1', condition: 'good' },
  { item_name: 'Wardrobe', room_name: 'Bedroom 1', condition: 'good' },
  { item_name: 'Sofa', room_name: 'Living Room', condition: 'good' },
  { item_name: 'TV', room_name: 'Living Room', condition: 'good' },
  { item_name: 'Fridge', room_name: 'Kitchen', condition: 'good' },
  { item_name: 'Washing Machine', room_name: 'Kitchen', condition: 'good' },
  { item_name: 'Microwave', room_name: 'Kitchen', condition: 'good' },
  { item_name: 'Kettle', room_name: 'Kitchen', condition: 'good' },
];

const conditionColour = (c: string) => c === 'new' ? '#16a34a' : c === 'good' ? '#0d9488' : c === 'fair' ? '#d97706' : '#dc2626';
const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-GB') : '—';
const needsCare = (i: InventoryItem) => i.condition === 'worn' || i.condition === 'fair';
const isGood = (i: InventoryItem) => i.condition === 'new' || i.condition === 'good';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');
.inv-wrap * { box-sizing: border-box; }
.inv-wrap { font-family: 'Bricolage Grotesque', system-ui, sans-serif; }
.toolbar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 20px; }
.search-box { display: flex; align-items: center; gap: 8px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 8px 14px; flex: 1; min-width: 180px; max-width: 260px; }
.search-box input { border: none; outline: none; font-size: 13px; font-family: inherit; color: #0f172a; background: transparent; width: 100%; }
.inv-filter { padding: 10px 36px 10px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff; font-size: 13px; font-weight: 700; color: #012169; appearance: none; min-width: 200px; cursor: pointer; font-family: inherit; }
.inv-alert-slim { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 12px 16px; border-radius: 12px; margin-bottom: 14px; font-size: 13px; font-weight: 700; }
.inv-alert-slim.amber { background: #fffbeb; border: 1.5px solid #fde68a; color: #92400e; }
.inv-alert-slim.red { background: #fef2f2; border: 1.5px solid #fecaca; color: #991b1b; }
.result-info { font-size: 12px; color: #94a3b8; font-weight: 600; margin-left: auto; }
.inv-table-wrap { border-radius: 14px; border: 1.5px solid #e9ecef; overflow: hidden; }
.inv-table { width: 100%; border-collapse: collapse; background: #fff; }
.inv-table th { padding: 11px 14px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #94a3b8; background: #f8fafc; border-bottom: 1.5px solid #e9ecef; white-space: nowrap; }
.inv-table td { padding: 13px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
.inv-table tbody tr:hover td { background: #fafbfc; }
.inv-table tbody tr.inv-edit-row td { background: #f0fdfa; border-bottom: 1.5px solid #0d9488; }
.item-name { font-size: 14px; font-weight: 700; color: #0f172a; }
.room-tag { font-size: 11px; font-weight: 700; color: #64748b; background: #f1f5f9; padding: 2px 8px; border-radius: 6px; margin-left: 6px; }
.cond-badge { display: inline-flex; padding: 3px 10px; border-radius: 7px; font-size: 12px; font-weight: 800; }
.btn-act { padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; border: 1.5px solid; font-family: inherit; white-space: nowrap; }
.inv-panel { background: #fff; border: 1.5px solid #0d9488; border-radius: 16px; padding: 24px; margin-bottom: 24px; }
.inv-panel-title { font-size: 16px; font-weight: 800; color: #0d9488; margin: 0 0 18px; }
.inv-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
.inv-grid .span2 { grid-column: span 2; }
.field-label { display: block; font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 5px; }
.field-input { width: 100%; padding: 9px 12px; border: 1.5px solid #d1d5db; border-radius: 9px; font-size: 14px; font-family: inherit; color: #0f172a; background: #fff; }
.field-input:focus { border-color: #0d9488; outline: none; }
.panel-actions { display: flex; gap: 10px; margin-top: 18px; flex-wrap: wrap; }
.btn-primary { background: #0d9488; color: #fff; border: none; border-radius: 9px; padding: 10px 24px; font-weight: 800; font-size: 14px; cursor: pointer; font-family: inherit; }
.btn-secondary { background: #fff; color: #374151; border: 1.5px solid #d1d5db; border-radius: 9px; padding: 10px 20px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; }
.toast-ok { background: #f0fdf4; color: #16a34a; border: 1.5px solid #bbf7d0; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }
.toast-err { background: #fff1f1; color: #dc2626; border: 1.5px solid #fecaca; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }
.hint-box { margin-top: 10px; padding: 10px 14px; background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 8px; font-size: 13px; color: #0d9488; font-weight: 600; }
.inv-empty { text-align: center; padding: 64px 32px; background: #fff; border: 2px dashed #e2e8f0; border-radius: 18px; }
.photo-thumb { width: 32px; height: 32px; border-radius: 6px; object-fit: cover; }
@media (max-width: 700px) { .inv-grid .span2 { grid-column: span 1; } .inv-table th:nth-child(3), .inv-table td:nth-child(3) { display: none; } }
`;

const BLANK = { property_address: '', room_name: '', item_name: '', condition: 'good', purchase_price: '', purchase_date: '', model_size: '', notes: '' };

export default function Inventory() {
  const { token } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<string[]>([]);
  const [filterTab, setFilterTab] = useState('All');
  const [search, setSearch] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [propertyRooms, setPropertyRooms] = useState<string[]>([]);
  const [itemForm, setItemForm] = useState({ ...BLANK });
  const [showNeedsCare, setShowNeedsCare] = useState(false);
  const [showEmptyProps, setShowEmptyProps] = useState(false);

  const loadRooms = async (address: string) => {
    if (!address || !token) { setPropertyRooms([]); return; }
    try {
      const r = await fetch(`${API_URL}/landlord/rooms?property_address=${encodeURIComponent(address)}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      const rooms = Array.isArray(data) ? data.map((rm: any) => rm.room_name || rm.room_number).filter(Boolean) : [];
      setPropertyRooms(rooms.length ? rooms : ROOM_NAMES);
    } catch { setPropertyRooms(ROOM_NAMES); }
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [iRes, pRes] = await Promise.all([
        fetch(`${API_URL}/landlord/inventory`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/landlord/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const [iData, pData] = await Promise.all([iRes.json(), pRes.json()]);
      setItems(Array.isArray(iData) ? iData : (iData.items || []));
      const propList: string[] = [...new Set<string>((pData.properties || []).map((p: any) => p.address as string))];
      setProperties(propList);
    } catch { setError('Failed to load inventory'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const st = location.state as { prefillProperty?: string; openAdd?: boolean };
    if (!st?.prefillProperty) return;
    setFilterTab(st.prefillProperty);
    setItemForm(p => ({ ...p, property_address: st.prefillProperty || '' }));
    loadRooms(st.prefillProperty);
    if (st.openAdd) setShowAddItem(true);
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const downloadPdf = async (address: string) => {
    const r = await fetch(`${API_URL}/landlord/inventory/pdf?property_address=${encodeURIComponent(address)}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `inventory-${address.replace(/[^a-z0-9]/gi, '-')}.pdf`;
    a.click(); URL.revokeObjectURL(url);
  };

  const uploadPhoto = async (id: number, file: File) => {
    const fd = new FormData(); fd.append('photo', file);
    await fetch(`${API_URL}/landlord/inventory/${id}/photo`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    load();
  };

  const addItem = async () => {
    if (!itemForm.property_address || !itemForm.item_name.trim()) { setError('Property and item name are required'); return; }
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API_URL}/landlord/inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...itemForm, purchase_price: itemForm.purchase_price ? Number(itemForm.purchase_price) : null }),
      });
      if (!r.ok) { const d = await r.json(); setError(d.error || 'Failed to save'); return; }
      setSuccess('Item saved'); setShowAddItem(false);
      setItemForm({ ...BLANK, property_address: itemForm.property_address });
      load(); setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to save'); }
    finally { setSaving(false); }
  };

  const addStarterKit = async (address: string) => {
    if (!address) { setError('Select a property first'); return; }
    if (!window.confirm(`Add ${STARTER_KIT.length} standard furnished items to ${address}?`)) return;
    setSaving(true); setError('');
    for (const kit of STARTER_KIT) {
      await fetch(`${API_URL}/landlord/inventory`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ property_address: address, ...kit }),
      });
    }
    setSuccess('Starter kit added'); load(); setSaving(false);
    setTimeout(() => setSuccess(''), 3000);
  };

  const deleteItem = async (id: number) => {
    if (!window.confirm('Delete this item?')) return;
    await fetch(`${API_URL}/landlord/inventory/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const startEdit = (item: InventoryItem) => {
    setEditingId(item.id);
    setEditForm({
      item_name: item.item_name,
      room_name: item.room_name || '',
      condition: item.condition,
      purchase_price: item.purchase_price != null ? String(item.purchase_price) : '',
      purchase_date: item.purchase_date ? item.purchase_date.split('T')[0] : '',
      model_size: item.model_size || '',
      notes: item.notes || '',
    });
  };

  const saveEdit = async () => {
    if (!editForm.item_name?.trim()) { setError('Item name is required'); return; }
    setSaving(true); setError('');
    const r = await fetch(`${API_URL}/landlord/inventory/${editingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...editForm, purchase_price: editForm.purchase_price ? Number(editForm.purchase_price) : null }),
    });
    if (!r.ok) { const d = await r.json(); setError(d.error || 'Failed to update'); setSaving(false); return; }
    setEditingId(null); load(); setSaving(false);
  };

  const activeProperty = filterTab !== 'All' && filterTab !== 'Needs care' && filterTab !== 'No photo' && properties.includes(filterTab) ? filterTab : '';

  const scoped = items.filter(i => {
    if (activeProperty && i.property_address !== activeProperty) return false;
    if (filterTab === 'Needs care' && !needsCare(i)) return false;
    if (filterTab === 'No photo' && i.photo_url) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    return i.item_name.toLowerCase().includes(q) || (i.room_name || '').toLowerCase().includes(q)
      || i.property_address.toLowerCase().includes(q) || (i.model_size || '').toLowerCase().includes(q);
  });

  const totalValue = scoped.reduce((s, i) => s + Number(i.purchase_price || 0), 0);
  const withPhotos = scoped.filter(i => i.photo_url).length;
  const careList = items.filter(needsCare);
  const emptyProps = properties.filter(p => !items.some(i => i.property_address === p));

  const filterCount = (f: string) => {
    if (f === 'Needs care') return items.filter(needsCare).length;
    if (f === 'No photo') return items.filter(i => !i.photo_url).length;
    if (properties.includes(f)) return items.filter(i => i.property_address === f).length;
    return 0;
  };

  const filterOptions = ['All', ...properties, 'Needs care', 'No photo'];

  return (
    <div className="mod-page inv-wrap">
      <style>{MODULE_PAGE_CSS}{CSS}</style>

      <div className="mod-banner-wrap mod-banner-inventory">
        <div className="mod-banner-glass">
          <div>
            <h1>📦 Inventory</h1>
            <p>{items.length} items · {properties.length} propert{properties.length === 1 ? 'y' : 'ies'} · £{items.reduce((s, i) => s + Number(i.purchase_price || 0), 0).toLocaleString()} invested — check-in ready</p>
          </div>
          <div className="mod-banner-actions">
            {activeProperty && (
              <button type="button" className="mod-btn-glass" onClick={() => downloadPdf(activeProperty)}>Check-in PDF</button>
            )}
            {activeProperty && (
              <button type="button" className="mod-btn-glass" onClick={() => addStarterKit(activeProperty)} disabled={saving}>Starter kit</button>
            )}
            <button type="button" className="mod-btn-white" onClick={() => { setShowAddItem(true); setError(''); }}>+ Add item</button>
          </div>
        </div>
      </div>

      <div className="mod-kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="mod-kpi mod-kpi-teal">
          <div className="kl">Items</div>
          <div className="kv">{scoped.length}</div>
          <div className="ks">{activeProperty ? 'this property' : 'all properties'}</div>
        </div>
        <div className="mod-kpi mod-kpi-blue">
          <div className="kl">Total value</div>
          <div className="kv" style={{ fontSize: totalValue >= 10000 ? 22 : 30 }}>£{totalValue.toLocaleString()}</div>
          <div className="ks">logged spend</div>
        </div>
        <div className="mod-kpi mod-kpi-green">
          <div className="kl">Good condition</div>
          <div className="kv">{scoped.filter(isGood).length}</div>
          <div className="ks">new or good</div>
        </div>
        <div className="mod-kpi mod-kpi-amber">
          <div className="kl">With photos</div>
          <div className="kv">{withPhotos}</div>
          <div className="ks">dispute-ready</div>
        </div>
      </div>

      {careList.length > 0 && !showNeedsCare && (
        <div className="inv-alert-slim amber">
          <span>⚠️ {careList.length} item{careList.length > 1 ? 's' : ''} fair or worn — consider replacing</span>
          <button type="button" className="btn-act" style={{ background: '#d97706', color: '#fff', borderColor: '#d97706' }}
            onClick={() => { setShowNeedsCare(true); setFilterTab('Needs care'); }}>View</button>
        </div>
      )}

      {emptyProps.length > 0 && !showEmptyProps && (
        <div className="inv-alert-slim red">
          <span>📋 {emptyProps.length} propert{emptyProps.length === 1 ? 'y has' : 'ies have'} no inventory — add before check-in</span>
          <button type="button" className="btn-act" style={{ background: '#012169', color: '#fff', borderColor: '#012169' }}
            onClick={() => setShowEmptyProps(true)}>View</button>
        </div>
      )}

      {showEmptyProps && emptyProps.length > 0 && (
        <div className="inv-alert-slim amber" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Properties without inventory</span>
            <button type="button" className="btn-act" style={{ background: '#fff', color: '#64748b', borderColor: '#e2e8f0' }} onClick={() => setShowEmptyProps(false)}>Hide</button>
          </div>
          {emptyProps.map(p => (
            <div key={p} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>{p}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn-act" style={{ background: '#0d9488', color: '#fff', borderColor: '#0d9488' }}
                  onClick={() => { setFilterTab(p); setItemForm(f => ({ ...f, property_address: p })); loadRooms(p); setShowAddItem(true); }}>+ Add item</button>
                <button className="btn-act" style={{ background: '#fff', color: '#012169', borderColor: '#93c5fd' }}
                  onClick={() => addStarterKit(p)}>Starter kit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="toast-err">❌ {error}</div>}
      {success && <div className="toast-ok">✅ {success}</div>}

      {showAddItem && (
        <div className="inv-panel">
          <div className="inv-panel-title">+ Add Inventory Item</div>
          <div className="inv-grid">
            <div>
              <label className="field-label">Property *</label>
              <select className="field-input" value={itemForm.property_address} onChange={e => { const addr = e.target.value; setItemForm(p => ({ ...p, property_address: addr })); loadRooms(addr); }}>
                <option value="">Select property…</option>
                {properties.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Location</label>
              <input list="room-list" className="field-input" value={itemForm.room_name} onChange={e => setItemForm(p => ({ ...p, room_name: e.target.value }))} placeholder="e.g. Bedroom 1" />
              <datalist id="room-list">{(propertyRooms.length ? propertyRooms : ROOM_NAMES).map(r => <option key={r} value={r} />)}</datalist>
            </div>
            <div>
              <label className="field-label">Item name *</label>
              <input list="item-list" className="field-input" value={itemForm.item_name} onChange={e => setItemForm(p => ({ ...p, item_name: e.target.value }))} placeholder="e.g. TV" />
              <datalist id="item-list">{COMMON_ITEMS.map(i => <option key={i} value={i} />)}</datalist>
            </div>
            <div>
              <label className="field-label">Model / size</label>
              <input className="field-input" value={itemForm.model_size} onChange={e => setItemForm(p => ({ ...p, model_size: e.target.value }))} placeholder='Samsung 50"' />
            </div>
            <div>
              <label className="field-label">Condition</label>
              <select className="field-input" value={itemForm.condition} onChange={e => setItemForm(p => ({ ...p, condition: e.target.value }))}>
                {CONDITIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Purchase price (£)</label>
              <input type="number" min="0" step="0.01" className="field-input" value={itemForm.purchase_price} onChange={e => setItemForm(p => ({ ...p, purchase_price: e.target.value }))} />
            </div>
            <div>
              <label className="field-label">Purchase date</label>
              <input type="date" className="field-input" value={itemForm.purchase_date} onChange={e => setItemForm(p => ({ ...p, purchase_date: e.target.value }))} />
            </div>
            <div className="span2">
              <label className="field-label">Notes</label>
              <input className="field-input" value={itemForm.notes} onChange={e => setItemForm(p => ({ ...p, notes: e.target.value }))} placeholder="Serial number, warranty…" />
            </div>
          </div>
          {itemForm.purchase_price && Number(itemForm.purchase_price) > 0 && (
            <div className="hint-box">✅ £{Number(itemForm.purchase_price).toFixed(2)} will auto-log to Accounting as Furniture / Equipment</div>
          )}
          <div className="panel-actions">
            <button onClick={addItem} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save item'}</button>
            <button onClick={() => { setShowAddItem(false); setError(''); }} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="toolbar">
          <div className="search-box">
            <span style={{ color: '#94a3b8' }}>🔍</span>
            <input placeholder="Search item, room, property…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="inv-filter" value={filterTab} onChange={e => setFilterTab(e.target.value)}>
            {filterOptions.map(f => (
              <option key={f} value={f}>{f}{f !== 'All' && filterCount(f) > 0 ? ` (${filterCount(f)})` : ''}</option>
            ))}
          </select>
          <span className="result-info">{scoped.length} shown</span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading inventory…</div>
      ) : items.length === 0 ? (
        <div className="inv-empty">
          <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
          <h3 style={{ margin: '0 0 8px' }}>No inventory yet</h3>
          <p style={{ color: '#94a3b8' }}>Track furniture and equipment per property — essential for check-in and deposit disputes.</p>
          {properties.length === 1 && (
            <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => addStarterKit(properties[0])}>Add starter kit</button>
          )}
          <button className="btn-secondary" style={{ marginTop: 10, marginLeft: 8 }} onClick={() => setShowAddItem(true)}>+ Add first item</button>
        </div>
      ) : scoped.length === 0 ? (
        <div className="inv-empty">
          <h3>No matches</h3>
          <button className="btn-secondary" onClick={() => { setFilterTab('All'); setSearch(''); }}>Clear filters</button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div className="inv-table-wrap">
            <table className="inv-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Property</th>
                  <th>Model</th>
                  <th>Condition</th>
                  <th>Price</th>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scoped.map(item => (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <span className="item-name">{item.item_name}</span>
                        {item.room_name && <span className="room-tag">{item.room_name}</span>}
                      </td>
                      <td style={{ fontSize: 13, color: '#64748b', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.property_address}</td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>{item.model_size || '—'}</td>
                      <td>
                        <span className="cond-badge" style={{ background: conditionColour(item.condition) + '18', color: conditionColour(item.condition) }}>
                          {item.condition.charAt(0).toUpperCase() + item.condition.slice(1)}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 700 }}>{item.purchase_price ? `£${Number(item.purchase_price).toFixed(2)}` : '—'}</td>
                      <td style={{ fontSize: 13, color: '#64748b' }}>{item.purchase_date ? fmt(item.purchase_date) : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {item.photo_url && <img src={item.photo_url} alt="" className="photo-thumb" />}
                          <label className="btn-act" style={{ background: '#f0fdfa', color: '#0d9488', borderColor: '#99f6e4', cursor: 'pointer' }}>
                            📷<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(item.id, f); }} />
                          </label>
                          <button className="btn-act" style={{ background: '#f0f9ff', color: '#1d4ed8', borderColor: '#93c5fd' }} onClick={() => startEdit(item)}>✏️</button>
                          <button className="btn-act" style={{ background: '#fff', color: '#dc2626', borderColor: '#fecaca' }} onClick={() => deleteItem(item.id)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                    {editingId === item.id && (
                      <tr className="inv-edit-row">
                        <td colSpan={7} style={{ padding: '18px 16px' }}>
                          <div className="inv-grid">
                            <div><label className="field-label">Item *</label><input className="field-input" value={editForm.item_name} onChange={e => setEditForm((p: any) => ({ ...p, item_name: e.target.value }))} /></div>
                            <div><label className="field-label">Location</label><input className="field-input" value={editForm.room_name} onChange={e => setEditForm((p: any) => ({ ...p, room_name: e.target.value }))} /></div>
                            <div><label className="field-label">Condition</label>
                              <select className="field-input" value={editForm.condition} onChange={e => setEditForm((p: any) => ({ ...p, condition: e.target.value }))}>
                                {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                            <div><label className="field-label">Model</label><input className="field-input" value={editForm.model_size} onChange={e => setEditForm((p: any) => ({ ...p, model_size: e.target.value }))} /></div>
                            <div><label className="field-label">Price (£)</label><input type="number" className="field-input" value={editForm.purchase_price} onChange={e => setEditForm((p: any) => ({ ...p, purchase_price: e.target.value }))} /></div>
                            <div><label className="field-label">Notes</label><input className="field-input" value={editForm.notes} onChange={e => setEditForm((p: any) => ({ ...p, notes: e.target.value }))} /></div>
                          </div>
                          <div className="panel-actions">
                            <button onClick={saveEdit} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save'}</button>
                            <button onClick={() => setEditingId(null)} className="btn-secondary">Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
